import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, ShieldCheck, CaretDown, CaretRight, Plus, Trash } from "@phosphor-icons/react";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

// Boundary so any unexpected render error still surfaces a message to the
// customer instead of a silent white screen.
class IntakeErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { try { console.error("Intake render error:", err, info); } catch {} }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h1 className="font-display text-2xl font-bold">Something went wrong loading this page.</h1>
            <p className="text-sm text-slate-500 mt-3">Please reply to your SoverenX Systems email so we can send you a fresh link.</p>
            <p className="text-[11px] text-slate-400 mt-4 font-mono break-all">{String(this.state.err.message || this.state.err)}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const TOPOLOGIES = [
  { key: "hub-and-spoke", label: "Hub & Spoke (HQ + branches)" },
  { key: "point-to-point", label: "Point-to-Point (2 sites)" },
  { key: "full-mesh", label: "Full Mesh (all-to-all)" },
  { key: "data-center", label: "Data Center (single site)" },
];
// Green = new build. Brown = anything touching an existing environment.
const WORK_TYPES = [
  { key: "green_new_deployment",  label: "New deployment (green field)",              bench: "green" },
  { key: "brown_migration",       label: "Migration — between or within vendors",     bench: "brown" },
  { key: "brown_add_services",    label: "Add services to an existing setup",         bench: "brown" },
  { key: "brown_prod_dr_failover",label: "PROD-DR failover / DR setup",               bench: "brown" },
  { key: "brown_dc_migration",    label: "DC migration — move services between DCs",  bench: "brown" },
  { key: "brown_firmware_upgrade",label: "Firmware / OS upgrade on live devices",     bench: "brown" },
  { key: "other",                 label: "Something else — details below",            bench: "" },
];
const VENDORS = ["Cisco", "Fortinet", "Palo Alto", "F5", "Checkpoint"];
const FEATURES = ["NGFW", "SD-WAN", "Core Switch", "Load Balancer", "Remote Hands", "24/7 Support"];
const BUDGETS = ["<$100k", "$100–250k", "$250–500k", "$500k+"];
const TIMELINES = ["ASAP", "30 days", "60 days", "90+ days"];

export default function IntakePage() {
  return (
    <IntakeErrorBoundary>
      {/* Toaster mounted here so validation / submit feedback is visible on the
          public /intake/{token} route (AppLayout isn't in play for public URLs). */}
      <Toaster position="top-center" richColors closeButton />
      <IntakeInner />
    </IntakeErrorBoundary>
  );
}

function IntakeInner() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // ---- Skippable-field infrastructure (Feb 2026) ----
  // Every previously "optional" field is now mandatory — but the customer can
  // explicitly mark it N/A via a small skip toggle. `skipped` = Set of field
  // names the customer chose to skip. On submit these become value "N/A" and
  // the field name is also forwarded to CC/PSDoc via `skipped_fields` for audit.
  const [skipped, setSkipped] = useState({});
  const isSkipped = (f) => !!skipped[f];
  const toggleSkip = (f, on) => setSkipped(s => on ? { ...s, [f]: true } : (({ [f]: _, ...rest }) => rest)(s));
  // Section-level N/A toggles (single click marks every sub-field as skipped)
  const [sectionNA, setSectionNA] = useState({});  // {contacts: true, attachments: true, sites: true}
  const setSectionSkip = (section, fields, on) => {
    setSectionNA(s => ({ ...s, [section]: on }));
    if (on) setSkipped(s => ({ ...s, ...Object.fromEntries(fields.map(f => [f, true])) }));
    else    setSkipped(s => { const c = { ...s }; fields.forEach(f => delete c[f]); return c; });
  };

  const [form, setForm] = useState({
    // ---- Agree / Disagree pattern (Feb 2026) ----
    // Master toggle presented to the customer AFTER they've read the presales
    // summary. Blank state = no answer picked yet (submit disabled).
    //   "agree"    → continue with full intake, becomes discovery_confirmed
    //   "disagree" → require feedback, link is locked until presales revises
    customer_agreement: "",
    customer_feedback: "",
    customer_notes: "",
    // { [question_index: string]: string } — customer's inline replies to the
    // presales engineer's clarifying questions (rendered in the discovery recap).
    question_answers: {},
    project_title: "",
    // ---- NEW: work type + brown-bench context (Feb 2026) ----
    work_type: "green_new_deployment",                // one of WORK_TYPES[].key
    brown_existing_environment: "",                    // free text describing what's already there
    brown_source_vendor: "",                           // e.g. "Cisco ASA + Meraki"
    brown_target_vendor: "",                           // e.g. "Fortinet FortiGate"
    brown_migration_scope: [],                         // ["Routing","Security","Wireless",...]
    brown_cutover_window: "",                          // free text, e.g. "Weekends 10pm–6am AEST"
    // ---- Existing greenfield fields ----
    topology: "hub-and-spoke", num_sites: 2,
    vendor_preference: "Fortinet", features_required: [],
    budget_range: "$100–250k", timeline: "60 days",
    technical_requirements: "", contact_name: "", contact_email: "", contact_phone: "",
    // extended fields
    business_driver: "", success_criteria: "",
    remote_users_avg: "", remote_users_peak: "",
    dual_wan_sdwan: "", cloud_onramp: [], realtime_apps: [],
    wireless_requirement: "", guest_wifi: false, management_platforms: "",
    site_list: [], network_age: "", network_pain_points: "",
    existing_isps: "", bandwidth_per_site: "",
    hours_of_operation: "", maintenance_windows: "",
    site_access_policy: "",
    business_contact_name: "", business_contact_email: "", business_contact_phone: "",
    escalation_contact_name: "", escalation_contact_email: "", escalation_contact_phone: "",
    diagram_url: "", config_export_url: "", other_docs_url: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await publicApi.get(`/intake/${token}`);
        setMeta(res.data);
        if (res.data.status === "submitted") setSubmitted(true);
        if (res.data.status === "revision_requested") {
          setRevisionBlocked({
            at: res.data.revision_requested_at,
            feedback: res.data.revision_feedback || "",
          });
        }
        // Pre-fill form with anything the presales engineer captured in PSDoc.
        // On revision links, also merge in the customer's previous submitted response
        // so they see (read-only) what they answered before and only edit deltas.
        const ts = res.data?.discovery?.tech_summary || {};
        const prev = res.data?.previous_response || null;
        const prefillForm = {};
        if (res.data.project_title) prefillForm.project_title = res.data.project_title;
        if (ts.topology)             prefillForm.topology = ts.topology;
        if (ts.num_sites != null && ts.num_sites !== "") prefillForm.num_sites = ts.num_sites;
        if (ts.vendor_preference)    prefillForm.vendor_preference = ts.vendor_preference;
        if (Array.isArray(ts.features_required) && ts.features_required.length) {
          prefillForm.features_required = ts.features_required;
        }
        // Merge previous customer response (round 2+) — carries all business/context fields
        if (prev && typeof prev === "object") {
          Object.assign(prefillForm, prev);
          // Reset agreement/feedback so customer must pick fresh for the revised notes
          prefillForm.customer_agreement = "";
          prefillForm.customer_feedback = "";
        }
        setForm(f => ({ ...f, ...prefillForm }));
      } catch (e) {
        setError(e.response?.data?.detail || "This invitation link is invalid or has expired.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const toggleFeature = (f) => setForm((s) => ({ ...s, features_required: s.features_required.includes(f) ? s.features_required.filter(x => x !== f) : [...s.features_required, f] }));

  const [revisionBlocked, setRevisionBlocked] = useState(null);

  const submit = async () => {
    if (!form.customer_agreement) {
      toast.error("Please tell us whether you agree or disagree with the summary above.");
      return;
    }
    if (form.customer_agreement === "disagree") {
      if (!form.customer_feedback.trim()) {
        toast.error("Please describe what needs to change so our presales team can revise the notes.");
        return;
      }
    } else if (meta?.is_revision) {
      // Round-2 Agree: the revision view doesn't render the full form. Skip the
      // per-field REQUIRED loop and only enforce project_title + email are set
      // (both are carried over from the customer's Round-1 response, so this
      // is defensive).
      if (!form.project_title || !form.contact_email) {
        toast.error("Missing project title or email from your Round-1 submission — please contact your presales rep.");
        return;
      }
    } else {
      // Agree branch — every required field must be either filled or explicitly skipped.
      // Grouped so we can surface the first missing field to the customer.
      const REQUIRED = [
        // Contact (email is always mandatory; not skippable)
        { field: "project_title", label: "Project Title", skippable: false },
        { field: "contact_email", label: "Your Email", skippable: false },
        // Constraints
        { field: "budget_range",  label: "Budget", skippable: false },
        { field: "timeline",      label: "Timeline", skippable: false },
        // Business Context
        { field: "business_driver",     label: "Business Driver" },
        { field: "success_criteria",    label: "Success Criteria" },
        { field: "environment_current", label: "Current Environment / Pain Points" },
        { field: "risks",               label: "Known Risks / Constraints" },
        // Extended Design Detail
        { field: "remote_users_avg",    label: "Remote users — avg concurrent" },
        { field: "remote_users_peak",   label: "Remote users — peak" },
        { field: "dual_wan_sdwan",      label: "Dual-WAN / SD-WAN?" },
        { field: "wireless_requirement",label: "Wireless" },
        { field: "management_platforms",label: "Existing mgmt platforms" },
        { field: "cloud_onramp",        label: "Cloud on-ramp", isArray: true },
        { field: "realtime_apps",       label: "Real-time apps", isArray: true },
        // Environment
        { field: "network_age",          label: "Current network age" },
        { field: "bandwidth_per_site",   label: "Bandwidth per site" },
        { field: "existing_isps",        label: "Existing ISPs / carriers" },
        { field: "network_pain_points",  label: "Network pain points / recent incidents" },
        { field: "hours_of_operation",   label: "Hours of operation" },
        { field: "maintenance_windows",  label: "Maintenance windows" },
        { field: "site_access_policy",   label: "Site access policy" },
        // Attachments
        { field: "diagram_url",       label: "Network diagram link" },
        { field: "config_export_url", label: "Current config export" },
        { field: "other_docs_url",    label: "Anything else useful" },
        // Additional Contacts — grouped under the section-level N/A toggle
        { field: "business_contact_name",   label: "Business contact — name" },
        { field: "business_contact_email",  label: "Business contact — email" },
        { field: "business_contact_phone",  label: "Business contact — phone" },
        { field: "escalation_contact_name", label: "Escalation contact — name" },
        { field: "escalation_contact_email",label: "Escalation contact — email" },
        { field: "escalation_contact_phone",label: "Escalation contact — phone" },
      ];
      for (const r of REQUIRED) {
        if (r.skippable === false) {
          const v = form[r.field];
          if (!v || (typeof v === "string" && !v.trim())) {
            toast.error(`${r.label} is required`);
            return;
          }
          continue;
        }
        if (skipped[r.field]) continue;
        const v = form[r.field];
        if (r.isArray) {
          if (!Array.isArray(v) || v.length === 0) {
            toast.error(`${r.label}: pick at least one option or mark N/A.`);
            return;
          }
        } else {
          if (v === "" || v === null || v === undefined || (typeof v === "string" && !v.trim())) {
            toast.error(`${r.label}: please answer or mark N/A.`);
            return;
          }
        }
      }
      // Site list — collapse to "N/A via section skip" or must have at least 1 row
      if (!sectionNA.sites && (!Array.isArray(form.site_list) || form.site_list.length === 0)) {
        toast.error("Add at least one site to the Site List, or mark the whole section as N/A.");
        return;
      }
    }
    try {
      // Coerce skipped fields to "N/A" so downstream (CC + PSDoc) sees the
      // explicit answer instead of an ambiguous empty value. Backend model
      // has structured shapes for some fields (e.g. site_list: List[SiteRow])
      // that cannot accept "N/A" strings — for those, keep the empty
      // container and rely on `skipped_fields` for the audit trail.
      const STRUCTURED_ARRAY_FIELDS = new Set(["site_list"]);
      const patched = { ...form };
      const skippedList = Object.keys(skipped);
      for (const f of skippedList) {
        if (STRUCTURED_ARRAY_FIELDS.has(f)) {
          patched[f] = [];
          continue;
        }
        const cur = patched[f];
        if (Array.isArray(cur))       patched[f] = ["N/A"];
        else if (typeof cur === "number" || cur === "" || cur == null) patched[f] = "N/A";
        else if (typeof cur === "string") patched[f] = "N/A";
      }
      // Number coercion — Pydantic Optional[int] rejects "" and "N/A"; send null.
      const numish = ["remote_users_avg", "remote_users_peak"];
      for (const nf of numish) {
        const v = patched[nf];
        if (v === "" || v === "N/A" || v == null) patched[nf] = null;
      }
      // Convert question_answers dict → ordered array matching meta.discovery.questions[]
      const orderedQA = (() => {
        const raw = meta?.discovery?.questions || [];
        const answers = form.question_answers || {};
        return raw.map((q, i) => ({
          question: typeof q === "string" ? q : (q?.text || q?.question || String(q || "")),
          answer: (answers[i] || "").trim(),
        }));
      })();
      const payload = { ...patched, skipped_fields: skippedList, question_answers: orderedQA };
      const res = await publicApi.post(`/intake/${token}/submit`, payload);
      setSubmitted(true);
      setMeta(m => ({ ...(m || {}), _submitted_agreement: res.data?.agreement || form.customer_agreement }));
      toast.success(res.data?.agreement === "disagree" ? "Feedback sent to presales" : "Submitted — thank you!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => (d?.msg || JSON.stringify(d))).join("; ")
        : (typeof detail === "string" ? detail : "Submission failed");
      toast.error(msg);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38] flex items-center justify-center"><div className="text-xs font-mono uppercase tracking-wider text-slate-500">Loading invitation…</div></div>;
  if (error) return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38] flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="opacity-50 flex justify-center"><BrandLogo variant="icon" /></div>
        <h1 className="font-display text-2xl font-bold mt-6">Link not valid</h1>
        <p className="text-sm text-slate-500 mt-2">{error}</p>
      </div>
    </div>
  );

  if (revisionBlocked) return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38] flex items-center justify-center p-8" data-testid="intake-revision-blocked">
      <div className="text-center max-w-lg">
        <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mx-auto">
          <ShieldCheck size={26} weight="duotone" className="text-amber-500" />
        </div>
        <div className="overline mt-6">Paused for Revision</div>
        <h1 className="font-display text-3xl font-bold mt-2">We're revising the notes.</h1>
        <p className="text-sm text-slate-500 mt-3">
          Thanks for flagging that the discovery summary wasn't quite right — our presales team is updating it now and we'll send you a fresh link once the notes reflect your feedback.
        </p>
        {revisionBlocked.feedback && (
          <div className="mt-6 rounded border border-amber-200 bg-amber-50 p-4 text-left">
            <div className="overline mb-1 text-amber-800">Your feedback</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">"{revisionBlocked.feedback}"</p>
          </div>
        )}
      </div>
    </div>
  );

  if (submitted) {
    const isDisagree = meta?._submitted_agreement === "disagree";
    return (
      <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38] flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className={`w-14 h-14 rounded-full ${isDisagree ? "bg-amber-500/10 border-amber-500/40" : "bg-emerald-500/10 border-emerald-500/40"} border flex items-center justify-center mx-auto`}>
            <CheckCircle size={26} weight="duotone" className={isDisagree ? "text-amber-500" : "text-emerald-400"} />
          </div>
          <div className="overline mt-6">{isDisagree ? "Feedback Received" : "Received"}</div>
          <h1 className="font-display text-3xl font-bold mt-2">Thank you.</h1>
          <p className="text-sm text-slate-500 mt-3">
            {isDisagree
              ? <>We've received your feedback for <span className="text-[#0F1E38]">{meta?.client?.company_name}</span>. Our presales team will revise the discovery notes and send you a fresh link shortly.</>
              : <>We've received your requirements for <span className="text-[#0F1E38]">{meta?.client?.company_name}</span>. We'll prepare your quote and get back to you shortly.</>
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38]">
      <header className="border-b border-slate-200 py-5 px-8">
        <div className="max-w-3xl mx-auto">
          <BrandLogo variant="full" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-10">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="overline">For · {meta?.client?.company_name}</div>
          {meta?.ps_code && (
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-blue-300 bg-blue-50 text-blue-800" title="Deal reference — please quote this in any emails with us.">
              ref · {meta.ps_code}
            </span>
          )}
          {meta?.discovery?.bench === "green" && (
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">green field</span>
          )}
          {meta?.discovery?.bench === "brown" && (
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">brown field</span>
          )}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Tell us about your project.</h1>
        <p className="text-sm text-slate-500 mt-3 max-w-2xl">
          Fill in the essentials. We'll turn this into a full solution design + Bill of Quantity within one business day.
          {meta?.note && <span className="block mt-2 text-[#0F1E38] italic">"{meta.note}"</span>}
        </p>

        {/* ---- Read-only discovery summary — what our engineer captured in the meeting ---- */}
        {(() => {
          // Coerce defensively so any bad data (dict items, nulls, etc.) can never
          // white-screen this customer-facing page.
          const rawQ = meta?.discovery?.questions;
          const qs = Array.isArray(rawQ)
            ? rawQ.map(q => (typeof q === "string" ? q : (q == null ? "" : (typeof q === "object" ? (q.text || q.question || JSON.stringify(q)) : String(q))))).filter(Boolean)
            : [];
          const notes = typeof meta?.discovery?.notes === "string" ? meta.discovery.notes : "";
          if (!notes && qs.length === 0) {
            return (
              <section className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-5" data-testid="intake-no-notes">
                <div className="overline mb-2 text-slate-600">Discovery Summary</div>
                <p className="text-sm text-slate-600 italic">
                  Our presales engineer hasn't shared discovery notes yet. Go ahead and fill in the form below — we'll match it up once notes arrive.
                </p>
              </section>
            );
          }
          return (
            <section className="mt-8 rounded-lg border-2 border-blue-300 bg-blue-50 p-6" data-testid="intake-discovery-summary">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="overline text-blue-800">
                  {meta?.is_revision ? `What we heard — Revised (Round ${meta.revision_round})` : "What we heard in the meeting"}
                </div>
                {meta?.is_revision && (
                  <span className="text-[10px] font-mono uppercase text-amber-800 bg-amber-100 border border-amber-300 px-2 py-1 rounded" data-testid="intake-revision-badge">
                    Revised · Round {meta.revision_round}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 mb-4 mt-2">
                {meta?.is_revision
                  ? "Our presales team has revised the discovery notes based on your earlier feedback. Please review the updated summary below and let us know if it's now on the right track."
                  : "Before you fill anything in, please read the summary below. These are the notes our presales engineer captured during your discovery call — plus a few questions they'd like your input on."}
              </p>
              {notes && (
                <div className="text-sm text-slate-800 whitespace-pre-wrap bg-white border border-blue-200 rounded p-4 shadow-sm">
                  {notes}
                </div>
              )}

              {/* ---- Tech spec (topology / sites / vendor / features) ---- */}
              {(() => {
                const ts = meta?.discovery?.tech_summary || {};
                const rows = [
                  ["Network Topology", ts.topology],
                  ["Number of Sites",  ts.num_sites],
                  ["Preferred Vendor", ts.vendor_preference],
                  ["Features Required", Array.isArray(ts.features_required) ? ts.features_required.join(", ") : ts.features_required],
                ].filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0));
                if (rows.length === 0) return null;
                return (
                  <div className="mt-4" data-testid="intake-tech-summary">
                    <div className="overline mb-2 text-blue-800">Technical Requirements our engineer captured</div>
                    <dl className="text-sm bg-white border border-blue-200 rounded p-4 shadow-sm space-y-2">
                      {rows.map(([k, v]) => (
                        <div key={k} className="flex flex-col sm:flex-row sm:gap-4">
                          <dt className="text-[11px] font-mono uppercase tracking-wider text-slate-500 sm:w-40 shrink-0">{k}</dt>
                          <dd className="text-slate-800 flex-1">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                );
              })()}

              {qs.length > 0 && (
                <div className="mt-4">
                  <div className="overline mb-2 text-blue-800">A few things we'd like your input on</div>
                  <p className="text-[11px] text-slate-600 mb-2 italic">Please answer each question inline. Leave blank or type <b>N/A</b> if it doesn't apply — every response goes straight to the presales engineer.</p>
                  <ol className="list-decimal list-inside space-y-3 text-sm text-slate-800 bg-white border border-blue-200 rounded p-4 shadow-sm">
                    {qs.map((q, i) => (
                      <li key={i} data-testid={`intake-question-${i}`}>
                        <span className="mr-1">{q}</span>
                        <Textarea
                          rows={2}
                          value={form.question_answers?.[i] || ""}
                          onChange={(e) => setForm({
                            ...form,
                            question_answers: { ...(form.question_answers || {}), [i]: e.target.value },
                          })}
                          placeholder="Your answer…"
                          className="mt-2 bg-slate-50 border-slate-300 text-sm"
                          data-testid={`intake-question-answer-${i}`}
                        />
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </section>
          );
        })()}

        {/* NOTE: The Agree/Disagree master toggle is rendered at the BOTTOM
            of the form (see below, above the submit button) so the customer
            reviews everything before deciding. Feb 2026 architecture. */}

        {form.customer_agreement === "disagree" && meta?.is_revision && (
          <p className="mt-4 text-[11px] text-slate-500 italic px-4">
            You submitted <b>Round {meta.revision_round - 1}</b> already — this is round <b>{meta.revision_round}</b>. When you send this feedback, this link will pause until presales revises the notes again.
          </p>
        )}

        {/* ============================================================
            RE-STRUCTURED FEB 2026:
            - Customer context (business context, environment, contacts) is
              ALWAYS visible on round 1 (even if they haven't clicked agree yet)
            - Agree/Disagree moves to the BOTTOM (rendered as a final decision)
            - On revision (round 2+): customer context is HIDDEN — they already
              gave those answers on round 1. Only recap + revised notes render.
           ============================================================ */}
        {!meta?.is_revision && (
        <div className="space-y-8 mt-6" data-testid="intake-agree-form">
          <section>
            <div className="overline mb-3">Project</div>
            <div><Label>Project Title *</Label><Input data-testid="intake-title" value={form.project_title} onChange={(e) => setForm({...form, project_title: e.target.value})} className="bg-slate-50 border-slate-300 mt-1" placeholder="A short name for this deployment"/></div>
            <p className="text-[11px] text-slate-500 italic mt-2">Note: Technical spec (topology · sites · vendor · features · requirements) was captured during our discovery meeting and is shown read-only in the summary above. If any of it needs to change, click <b>Disagree</b> at the bottom and tell us what to revise.</p>
          </section>

          <section>
            <div className="overline mb-3">Constraints</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Budget *</Label>
                <Select value={form.budget_range} onValueChange={(v) => setForm({...form, budget_range: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-300 mt-1" data-testid="intake-budget"><SelectValue/></SelectTrigger>
                  <SelectContent>{BUDGETS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Timeline *</Label>
                <Select value={form.timeline} onValueChange={(v) => setForm({...form, timeline: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-300 mt-1" data-testid="intake-timeline"><SelectValue/></SelectTrigger>
                  <SelectContent>{TIMELINES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* ---- Business Context — required ---- */}
          <Collapsible title="Business Context (required)" testid="intake-section-business" defaultOpen required>
            <p className="text-[11px] text-slate-500 italic mb-3">Every field is required. If something genuinely doesn't apply, tick <b>N/A</b> next to it.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <SkippableField field="business_driver" label="Business Driver" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Textarea rows={2} disabled={disabled} value={form.business_driver} onChange={(e) => setForm({ ...form, business_driver: e.target.value })} data-testid="intake-business-driver" placeholder="Why are we doing this?" className="bg-slate-50 border-slate-300" />
                )}
              </SkippableField>
              <SkippableField field="success_criteria" label="Success Criteria" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Textarea rows={2} disabled={disabled} value={form.success_criteria} onChange={(e) => setForm({ ...form, success_criteria: e.target.value })} data-testid="intake-success" placeholder="What does 'done well' look like?" className="bg-slate-50 border-slate-300" />
                )}
              </SkippableField>
              <SkippableField field="environment_current" label="Current Environment / Pain Points" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Textarea rows={2} disabled={disabled} value={form.environment_current} onChange={(e) => setForm({ ...form, environment_current: e.target.value })} data-testid="intake-environment" placeholder="What's in place today, what hurts?" className="bg-slate-50 border-slate-300" />
                )}
              </SkippableField>
              <SkippableField field="risks" label="Known Risks / Constraints" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Textarea rows={2} disabled={disabled} value={form.risks} onChange={(e) => setForm({ ...form, risks: e.target.value })} data-testid="intake-risks" placeholder="Freeze windows, regulatory, staffing…" className="bg-slate-50 border-slate-300" />
                )}
              </SkippableField>
            </div>
          </Collapsible>

          {/* Brown-bench context — only shown when the customer picked a brown / other work type */}
          {(() => {
            const wt = WORK_TYPES.find(w => w.key === form.work_type);
            const isBrown = wt && (wt.bench === "brown" || wt.bench === "");
            if (!isBrown) return null;
            const isMigration = form.work_type === "brown_migration" || form.work_type === "brown_dc_migration";
            return (
              <section data-testid="intake-brown-section" className="rounded-lg border border-amber-200 bg-amber-50/40 p-4">
                <div className="overline mb-3 text-amber-800">Existing Environment</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>What's already in place?</Label>
                    <Textarea
                      rows={3}
                      value={form.brown_existing_environment}
                      onChange={(e) => setForm({ ...form, brown_existing_environment: e.target.value })}
                      placeholder="e.g. 12 branches on Cisco Meraki MX, DC in Equinix SY3 running ASA firewalls + Catalyst core. VPN to AWS via IPsec."
                      className="bg-white border-slate-300 mt-1"
                      data-testid="intake-brown-existing"
                    />
                  </div>
                  {isMigration && (
                    <>
                      <div>
                        <Label>Source (from)</Label>
                        <Input
                          value={form.brown_source_vendor}
                          onChange={(e) => setForm({ ...form, brown_source_vendor: e.target.value })}
                          placeholder="e.g. Cisco ASA 5525-X"
                          className="bg-white border-slate-300 mt-1"
                          data-testid="intake-brown-source"
                        />
                      </div>
                      <div>
                        <Label>Target (to)</Label>
                        <Input
                          value={form.brown_target_vendor}
                          onChange={(e) => setForm({ ...form, brown_target_vendor: e.target.value })}
                          placeholder="e.g. Fortinet FortiGate 200F"
                          className="bg-white border-slate-300 mt-1"
                          data-testid="intake-brown-target"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Migration scope</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {["Routing","Security","Wireless","SD-WAN","Load Balancer","VPN","Cloud on-ramp"].map(s => {
                            const on = form.brown_migration_scope.includes(s);
                            return (
                              <button
                                key={s} type="button"
                                data-testid={`intake-brown-scope-${s}`}
                                onClick={() => setForm({ ...form, brown_migration_scope: on ? form.brown_migration_scope.filter(x=>x!==s) : [...form.brown_migration_scope, s] })}
                                className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider border ${on ? "bg-amber-500/20 border-amber-500 text-amber-800" : "border-slate-300 text-slate-500 hover:text-[#0F1E38]"}`}
                              >{s}</button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="md:col-span-2">
                    <Label>Preferred cutover / maintenance window</Label>
                    <Input
                      value={form.brown_cutover_window}
                      onChange={(e) => setForm({ ...form, brown_cutover_window: e.target.value })}
                      placeholder="e.g. Weekends 10pm–6am AEST. No changes during EOFY."
                      className="bg-white border-slate-300 mt-1"
                      data-testid="intake-brown-cutover"
                    />
                  </div>
                </div>
              </section>
            );
          })()}

          {/* NOTE (Feb 2026): The legacy "Project (topology/sites)", "Vendor & Features",
              duplicate "Constraints", "Technical Requirements", and the collapsible
              "Business Context" sections were removed here. Presales captures the tech
              spec in the discovery meeting (rendered read-only in the summary card
              above), and Business Context is now a required section rendered above.
              If the customer disagrees with the presales spec, they use the
              Disagree → Feedback path at the bottom of the form. ---- */}

          {/* ---- Extended technical design ---- */}
          <Collapsible title="Extended Design Detail (required)" testid="intake-section-design" defaultOpen required>
            <p className="text-[11px] text-slate-500 italic mb-3">Every field is required. Tick <b>N/A</b> if a specific answer doesn't apply to you.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <SkippableField field="remote_users_avg" label="Remote users — avg concurrent" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input type="number" disabled={disabled} value={form.remote_users_avg} onChange={(e) => setForm({...form, remote_users_avg: e.target.value})} className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="remote_users_peak" label="Remote users — peak" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input type="number" disabled={disabled} value={form.remote_users_peak} onChange={(e) => setForm({...form, remote_users_peak: e.target.value})} className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="dual_wan_sdwan" label="Dual-WAN / SD-WAN?" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Select value={form.dual_wan_sdwan} onValueChange={(v) => setForm({...form, dual_wan_sdwan: v})} disabled={disabled}>
                    <SelectTrigger className="bg-slate-50 border-slate-300"><SelectValue placeholder="Select…"/></SelectTrigger>
                    <SelectContent>{["Yes","No","Exploring"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </SkippableField>
              <SkippableField field="cloud_onramp" label="Cloud on-ramp needed (tick all)" skipped={skipped} onToggleSkip={toggleSkip} className="md:col-span-3">
                {({ disabled }) => (
                  <MultiChips options={["AWS","Azure","GCP","Oracle","Hybrid","None"]} value={form.cloud_onramp} onChange={(v) => !disabled && setForm({...form, cloud_onramp: v})} testid="intake-cloud"/>
                )}
              </SkippableField>
              <SkippableField field="realtime_apps" label="Real-time apps (QoS drivers)" skipped={skipped} onToggleSkip={toggleSkip} className="md:col-span-3">
                {({ disabled }) => (
                  <MultiChips options={["Teams","Zoom","Genesys","Custom SIP","Other"]} value={form.realtime_apps} onChange={(v) => !disabled && setForm({...form, realtime_apps: v})} testid="intake-realtime"/>
                )}
              </SkippableField>
              <SkippableField field="wireless_requirement" label="Wireless" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Select value={form.wireless_requirement} onValueChange={(v) => setForm({...form, wireless_requirement: v})} disabled={disabled}>
                    <SelectTrigger className="bg-slate-50 border-slate-300"><SelectValue placeholder="Select…"/></SelectTrigger>
                    <SelectContent>{["New","Refresh","None"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </SkippableField>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.guest_wifi} onChange={(e) => setForm({...form, guest_wifi: e.target.checked})}/> Guest Wi-Fi required
                </label>
              </div>
              <SkippableField field="management_platforms" label="Existing mgmt platforms" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.management_platforms} onChange={(e) => setForm({...form, management_platforms: e.target.value})} placeholder="FortiManager, Panorama, Meraki…" className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
            </div>
          </Collapsible>

          {/* ---- Environment ---- */}
          <Collapsible title="Environment (required)" testid="intake-section-env" defaultOpen required>
            <p className="text-[11px] text-slate-500 italic mb-3">Every field is required. Tick <b>N/A</b> next to any that don't apply.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <SkippableField field="network_age" label="Current network age" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Select value={form.network_age} onValueChange={(v) => setForm({...form, network_age: v})} disabled={disabled}>
                    <SelectTrigger className="bg-slate-50 border-slate-300"><SelectValue placeholder="Select…"/></SelectTrigger>
                    <SelectContent>{["<2 years","2-5 years","5-10 years",">10 years"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </SkippableField>
              <SkippableField field="bandwidth_per_site" label="Bandwidth per site (current + target)" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.bandwidth_per_site} onChange={(e) => setForm({...form, bandwidth_per_site: e.target.value})} placeholder="e.g. HQ 1Gbps → 5Gbps" className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="existing_isps" label="Existing ISPs / carriers (per site + contract end if relevant)" skipped={skipped} onToggleSkip={toggleSkip} className="md:col-span-2">
                {({ disabled }) => (
                  <Textarea rows={2} disabled={disabled} value={form.existing_isps} onChange={(e) => setForm({...form, existing_isps: e.target.value})} className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="network_pain_points" label="Network pain points / recent incidents" skipped={skipped} onToggleSkip={toggleSkip} className="md:col-span-2">
                {({ disabled }) => (
                  <Textarea rows={2} disabled={disabled} value={form.network_pain_points} onChange={(e) => setForm({...form, network_pain_points: e.target.value})} className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="hours_of_operation" label="Hours of operation" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.hours_of_operation} onChange={(e) => setForm({...form, hours_of_operation: e.target.value})} placeholder="e.g. Mon-Fri 8am-6pm AEST" className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="maintenance_windows" label="Available maintenance windows" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.maintenance_windows} onChange={(e) => setForm({...form, maintenance_windows: e.target.value})} placeholder="Sat 10pm-2am, monthly" className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
              <SkippableField field="site_access_policy" label="Site access policy (badge, escort, PPE, police check…)" skipped={skipped} onToggleSkip={toggleSkip} className="md:col-span-2">
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.site_access_policy} onChange={(e) => setForm({...form, site_access_policy: e.target.value})} className="bg-slate-50 border-slate-300"/>
                )}
              </SkippableField>
            </div>
            {/* Site List with section-level "No specific sites yet" toggle */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-700">Site List{!sectionNA.sites && <span className="text-rose-500 ml-1">*</span>}</div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1 cursor-pointer select-none" data-testid="skip-section-sites">
                  <input type="checkbox" checked={!!sectionNA.sites} onChange={(e) => setSectionSkip("sites", ["site_list"], e.target.checked)} />
                  No specific sites yet — N/A
                </label>
              </div>
              {sectionNA.sites
                ? <div className="text-[11px] text-slate-400 italic border border-dashed border-slate-200 rounded p-3">Section marked as not applicable.</div>
                : <SiteListEditor sites={form.site_list} onChange={(sl) => setForm({...form, site_list: sl})}/>}
            </div>
          </Collapsible>

          {/* ---- Attachments (URL fields) ---- */}
          <Collapsible title="Attachments (required)" testid="intake-section-attachments" defaultOpen required>
            <p className="text-[11px] text-slate-500 italic mb-3">Paste share links to any docs that would help our engineer. Tick <b>N/A</b> if you don't have one.</p>
            <div className="space-y-3">
              <SkippableField field="diagram_url" label="Network diagram link" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.diagram_url} onChange={(e) => setForm({...form, diagram_url: e.target.value})} placeholder="Google Drive / Dropbox / SharePoint URL" className="bg-slate-50 border-slate-300 font-mono text-xs"/>
                )}
              </SkippableField>
              <SkippableField field="config_export_url" label="Current config export" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.config_export_url} onChange={(e) => setForm({...form, config_export_url: e.target.value})} className="bg-slate-50 border-slate-300 font-mono text-xs"/>
                )}
              </SkippableField>
              <SkippableField field="other_docs_url" label="Anything else useful" skipped={skipped} onToggleSkip={toggleSkip}>
                {({ disabled }) => (
                  <Input disabled={disabled} value={form.other_docs_url} onChange={(e) => setForm({...form, other_docs_url: e.target.value})} className="bg-slate-50 border-slate-300 font-mono text-xs"/>
                )}
              </SkippableField>
            </div>
          </Collapsible>

          {/* ---- Extra contacts ---- */}
          <Collapsible title="Additional contacts (required)" testid="intake-section-contacts" defaultOpen required>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] text-slate-500 italic">Fill in both contacts, or mark the whole section as N/A.</p>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1 cursor-pointer select-none" data-testid="skip-section-contacts">
                <input
                  type="checkbox"
                  checked={!!sectionNA.contacts}
                  onChange={(e) => setSectionSkip("contacts",
                    ["business_contact_name","business_contact_email","business_contact_phone",
                     "escalation_contact_name","escalation_contact_email","escalation_contact_phone"],
                    e.target.checked)}
                />
                No additional contacts — N/A
              </label>
            </div>
            {sectionNA.contacts ? (
              <div className="text-[11px] text-slate-400 italic border border-dashed border-slate-200 rounded p-3">Section marked as not applicable.</div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-3 text-[11px] uppercase text-slate-500 font-mono tracking-wider">Business / Procurement</div>
                <SkippableField field="business_contact_name" label="Name" skipped={skipped} onToggleSkip={toggleSkip}>
                  {({ disabled }) => <Input disabled={disabled} value={form.business_contact_name} onChange={(e) => setForm({...form, business_contact_name: e.target.value})} className="bg-slate-50 border-slate-300"/>}
                </SkippableField>
                <SkippableField field="business_contact_email" label="Email" skipped={skipped} onToggleSkip={toggleSkip}>
                  {({ disabled }) => <Input type="email" disabled={disabled} value={form.business_contact_email} onChange={(e) => setForm({...form, business_contact_email: e.target.value})} className="bg-slate-50 border-slate-300"/>}
                </SkippableField>
                <SkippableField field="business_contact_phone" label="Phone" skipped={skipped} onToggleSkip={toggleSkip}>
                  {({ disabled }) => <Input disabled={disabled} value={form.business_contact_phone} onChange={(e) => setForm({...form, business_contact_phone: e.target.value})} className="bg-slate-50 border-slate-300"/>}
                </SkippableField>
                <div className="md:col-span-3 text-[11px] uppercase text-slate-500 font-mono tracking-wider mt-2">Escalation / After-hours</div>
                <SkippableField field="escalation_contact_name" label="Name" skipped={skipped} onToggleSkip={toggleSkip}>
                  {({ disabled }) => <Input disabled={disabled} value={form.escalation_contact_name} onChange={(e) => setForm({...form, escalation_contact_name: e.target.value})} className="bg-slate-50 border-slate-300"/>}
                </SkippableField>
                <SkippableField field="escalation_contact_email" label="Email" skipped={skipped} onToggleSkip={toggleSkip}>
                  {({ disabled }) => <Input type="email" disabled={disabled} value={form.escalation_contact_email} onChange={(e) => setForm({...form, escalation_contact_email: e.target.value})} className="bg-slate-50 border-slate-300"/>}
                </SkippableField>
                <SkippableField field="escalation_contact_phone" label="Phone" skipped={skipped} onToggleSkip={toggleSkip}>
                  {({ disabled }) => <Input disabled={disabled} value={form.escalation_contact_phone} onChange={(e) => setForm({...form, escalation_contact_phone: e.target.value})} className="bg-slate-50 border-slate-300"/>}
                </SkippableField>
              </div>
            )}
          </Collapsible>

          <section>
            <div className="overline mb-3">Your Contact Details</div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Your Name</Label><Input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
              <div><Label>Email *</Label><Input data-testid="intake-email" type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
              <div><Label>Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
            </div>
          </section>

          {/* ---- Additional Notes from Customer (added Feb 2026) ---- */}
          <section data-testid="intake-customer-notes-section">
            <div className="overline mb-3">Anything else we should know?</div>
            <p className="text-xs text-slate-500 mb-2">A brief general note — timing constraints, internal stakeholders, questions of your own. Optional.</p>
            <Textarea
              data-testid="intake-customer-notes"
              rows={3}
              value={form.customer_notes}
              onChange={(e) => setForm({ ...form, customer_notes: e.target.value })}
              placeholder="e.g. Please copy our CIO on the proposal. We'd also like a quick 15-min briefing before the design workshop."
              className="bg-slate-50 border-slate-300"
            />
          </section>
        </div>
        )}

        {/* ============================================================
            REVISION MODE — round 2+ minimal form.
            Shows a read-only recap of what customer already agreed to,
            then the revised discovery notes for a fresh agree/disagree.
           ============================================================ */}
        {meta?.is_revision && (
          <div className="space-y-6 mt-6" data-testid="intake-revision-form">
            <section className="rounded-lg border border-slate-200 bg-slate-50 p-5" data-testid="intake-previous-recap">
              <div className="overline mb-3 text-slate-600">Previously submitted (read-only recap · Round {(meta?.revision_round || 2) - 1})</div>
              <p className="text-xs text-slate-500 mb-3">
                You reviewed our earlier notes and provided the following context. We'll carry these answers forward — you don't need to re-type them. Just review the <b>revised summary above</b> and let us know if it's on the right track.
              </p>
              {meta.previous_response && (
                <div className="text-xs text-slate-700 space-y-1.5 bg-white border border-slate-200 rounded p-3">
                  {meta.previous_response.project_title && <div><b>Project:</b> {meta.previous_response.project_title}</div>}
                  {meta.previous_response.contact_email && <div><b>Your email:</b> {meta.previous_response.contact_email}</div>}
                  {meta.previous_response.timeline && <div><b>Timeline:</b> {meta.previous_response.timeline}</div>}
                  {meta.previous_response.budget_range && <div><b>Budget:</b> {meta.previous_response.budget_range}</div>}
                  {meta.previous_response.business_driver && <div><b>Business context:</b> {meta.previous_response.business_driver}</div>}
                  {meta.previous_response.success_criteria && <div><b>Success looks like:</b> {meta.previous_response.success_criteria}</div>}
                  {meta.previous_response.technical_requirements && <div><b>Tech requirements:</b> {meta.previous_response.technical_requirements}</div>}
                  {meta.previous_response.customer_feedback && <div className="text-amber-700"><b>Feedback you gave:</b> "{meta.previous_response.customer_feedback}"</div>}
                </div>
              )}
            </section>
          </div>
        )}

        {/* ============================================================
            AGREE / DISAGREE  ·  final decision at the bottom of the form.
            Renders for BOTH round 1 and round N — always visible.
           ============================================================ */}
        <section className="mt-8 rounded-lg border-2 border-slate-300 bg-white p-6" data-testid="intake-agreement-section">
          <div className="overline mb-2">
            {meta?.is_revision
              ? `Round ${meta?.revision_round || 2} — do you agree with the revised summary?`
              : "One last thing — do you agree with our discovery summary above?"}
          </div>
          <p className="text-xs text-slate-500 mb-4">
            {meta?.is_revision
              ? "Please review the revised summary above and let us know if it now reflects your requirements."
              : "Please confirm the summary above reflects what you asked us for. Disagree if we've missed anything material."}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="intake-agree-btn"
              onClick={() => setForm({ ...form, customer_agreement: "agree" })}
              className={`px-4 py-4 rounded-lg border-2 text-left transition ${form.customer_agreement === "agree" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-400"}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full border-2 ${form.customer_agreement === "agree" ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                  {form.customer_agreement === "agree" && <CheckCircle size={12} weight="fill" className="text-white" />}
                </div>
                <span className="font-display font-bold text-[#0F1E38]">Agree — this looks right</span>
              </div>
              <div className="text-xs text-slate-500 mt-1.5">We'll proceed with your quote.</div>
            </button>
            <button
              type="button"
              data-testid="intake-disagree-btn"
              onClick={() => setForm({ ...form, customer_agreement: "disagree" })}
              className={`px-4 py-4 rounded-lg border-2 text-left transition ${form.customer_agreement === "disagree" ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-400"}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full border-2 ${form.customer_agreement === "disagree" ? "border-amber-500 bg-amber-500" : "border-slate-300"}`} />
                <span className="font-display font-bold text-[#0F1E38]">Disagree — needs changes</span>
              </div>
              <div className="text-xs text-slate-500 mt-1.5">Tell us what to fix and we'll revise.</div>
            </button>
          </div>

          {form.customer_agreement === "disagree" && (
            <div className="mt-5 border-t border-slate-200 pt-4" data-testid="intake-disagree-panel">
              <Label>What needs to change? *</Label>
              <Textarea
                data-testid="intake-feedback"
                rows={4}
                value={form.customer_feedback}
                onChange={(e) => setForm({ ...form, customer_feedback: e.target.value })}
                placeholder="e.g. Please cost Fortinet AND Palo Alto so we can compare."
                className="bg-slate-50 border-slate-300 mt-2"
              />
              <p className="text-[11px] text-slate-500 mt-2 italic">
                When you submit, this link will be paused until presales revises the notes. You'll receive a fresh link once they're ready.
              </p>
            </div>
          )}

          <Button
            data-testid="intake-submit-btn"
            onClick={submit}
            disabled={!form.customer_agreement || (form.customer_agreement === "disagree" && !form.customer_feedback.trim())}
            className={`w-full h-12 mt-6 font-mono uppercase tracking-wider text-xs ${form.customer_agreement === "disagree" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38]"} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {form.customer_agreement === "disagree" ? "Send Feedback to Presales →" : "Submit Requirements →"}
          </Button>

          <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-3">
            <ShieldCheck size={12} className="text-emerald-400" /> One-time link · Your info is stored securely
          </div>
        </section>
      </main>
    </div>
  );
}

// ---- Reusable helpers for the extended intake sections ----

function Collapsible({ title, children, testid, defaultOpen = false, required = false }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <section className="border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition"
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} className="text-slate-500" /> : <CaretRight size={14} className="text-slate-500" />}
          <span className="text-xs font-mono uppercase tracking-widest text-slate-700">{title}</span>
        </div>
        {required
          ? <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">required</span>
          : <span className="text-[10px] text-slate-400">optional</span>}
      </button>
      {open && <div className="p-4 bg-white">{children}</div>}
    </section>
  );
}

function MultiChips({ options, value, onChange, testid }) {
  const toggle = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2 mt-1" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider border transition ${
            value.includes(o)
              ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-700"
              : "border-slate-300 text-slate-600 hover:text-[#0F1E38]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// Skip toggle used inline with any input. When on, we visually grey the
// wrapper and the parent form treats the field as satisfied (value → "N/A"
// on submit, and the field name is added to `skipped_fields` for audit).
function SkipToggle({ field, on, onChange }) {
  return (
    <label
      className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1 cursor-pointer select-none"
      data-testid={`skip-toggle-${field}`}
    >
      <input
        type="checkbox"
        checked={!!on}
        onChange={(e) => onChange(e.target.checked)}
        className="cursor-pointer"
        data-testid={`skip-${field}`}
      />
      N/A
    </label>
  );
}

// Wraps a single input: renders label + N/A toggle in the header row,
// then the input itself. When skipped, the input is disabled + dimmed and
// a small "Skipped — not applicable" note appears below.
function SkippableField({ field, label, required = true, skipped, onToggleSkip, className = "", children }) {
  const isSkipped = !!skipped[field];
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1">
        <Label className={isSkipped ? "text-slate-400" : ""}>
          {label}
          {required && !isSkipped && <span className="text-rose-500 ml-1">*</span>}
        </Label>
        <SkipToggle field={field} on={isSkipped} onChange={(v) => onToggleSkip(field, v)} />
      </div>
      <div className={isSkipped ? "opacity-40 pointer-events-none" : ""}>
        {children({ disabled: isSkipped })}
      </div>
      {isSkipped && <div className="mt-1 text-[10px] text-slate-400 italic">Marked as not applicable.</div>}
    </div>
  );
}


function SiteListEditor({ sites, onChange }) {
  const add = () => onChange([...(sites || []), { city: "", type: "Branch", users: "" }]);
  const update = (idx, field, val) => {
    const next = [...sites];
    next[idx] = { ...next[idx], [field]: val };
    onChange(next);
  };
  const remove = (idx) => onChange(sites.filter((_, i) => i !== idx));

  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="site-list-editor">
      <div className="flex items-center justify-between mb-2">
        <Label>Site list (city + type + rough user count)</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="site-add-btn">
          <Plus size={12} className="mr-1" /> Add site
        </Button>
      </div>
      {sites.length === 0 && <div className="text-xs text-slate-400 italic">No sites added yet. Click "Add site" for each location.</div>}
      <div className="space-y-2">
        {sites.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`site-row-${i}`}>
            <Input placeholder="City" value={s.city} onChange={(e) => update(i, "city", e.target.value)} className="col-span-5 bg-slate-50 border-slate-300 h-9 text-sm" />
            <Select value={s.type} onValueChange={(v) => update(i, "type", v)}>
              <SelectTrigger className="col-span-3 bg-slate-50 border-slate-300 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{["HQ","Branch","DC","Remote"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Users" type="number" value={s.users} onChange={(e) => update(i, "users", e.target.value)} className="col-span-3 bg-slate-50 border-slate-300 h-9 text-sm" />
            <Button type="button" size="sm" variant="outline" onClick={() => remove(i)} className="col-span-1 text-red-600 h-9 px-2"><Trash size={12} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

